import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  FormSelect,
  getUserName,
  NoContentItem,
  TextSecondary,
  UserDto,
  UserValidFormDataType,
  useSearchNewUsersQuery,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import {
  FC,
  PropsWithChildren,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import translations from './NewUserFormAutoComplete.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'new-user-form-autocomplete',
);
addTranslations(translations);

export interface NewUserFormAutoCompleteProps {
  name: string;
  placeholder: string;
}

interface UserOption {
  value: string;
  label: ReactNode;
  userData: UserDto;
}

export const NewUserFormAutoComplete: FC<
  PropsWithChildren<NewUserFormAutoCompleteProps>
> = (props) => {
  const { name, placeholder } = props;
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');

  const { data } = useSearchNewUsersQuery(
    { searchText },
    {
      enabled: searchText.length > 1,
    },
  );

  const options: UserOption[] | undefined = useMemo(
    () =>
      data?.map((userData) => ({
        value: userData.username,
        label: (
          <Flex gap="small" align="center">
            {getUserName(userData)}
            <TextSecondary>{userData.username}</TextSecondary>
          </Flex>
        ),
        userData,
      })),
    [data],
  );

  const { setValue } = useFormContext<UserValidFormDataType>();

  const handleReset = useCallback(() => {
    setSearchText('');
    setValue('firstName', '');
    setValue('lastName', '');
  }, [setSearchText, setValue]);

  const handleSelect = useCallback(
    (_value: string, { userData }: UserOption) => {
      setValue('firstName', userData.firstName);
      setValue('lastName', userData.lastName);
    },
    [setValue],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchText(value);
    },
    [setSearchText],
  );

  return (
    <FormSelect
      allowClear
      showSearch
      name={name}
      notFoundContent={
        searchText.length < 2 ? (
          <NoContentItem label={t('search-limit')} />
        ) : (
          <NoContentItem />
        )
      }
      placeholder={placeholder}
      options={options}
      onClear={handleReset}
      onSelect={handleSelect}
      onFocus={handleReset}
      onSearch={handleSearch}
      filterOption={false}
    />
  );
};
